/**
 * `attackIntent` — the **host-agnostic intent builders** for the Skirmish/Activate action bar.
 *
 * The builders have **zero** spatial dependency — they only pack the up-front declarations into the
 * wire intent — so this module carries no grid/geometry import at all. COMBAT.app's action bar
 * targets by **clicking a token on the canvas** and calls these directly; its square-grid targeting
 * hint is a separate cheap Chebyshev distance advisory (`combatInspector.ts`).
 *
 * **The client never resolves** (the load-bearing rule): these builders only *advise the wire*;
 * the authority rolls and records. Only non-default fields are included so the wire stays minimal
 * (an empty `choices` is dropped — the authority's no-prompt fast path).
 */
import type { HeldAction, WeaponMode, WeaponProfile } from "@lancer-vtt/domain";
import type { AttackTarget, EncounterIntent, EndAttack, RequestActivate, RequestAttack, RequestBarrage, RequestBootUp, RequestDetectMine, RequestDetonateMine, RequestDisarmMine, RequestDismount, RequestDisengage, RequestEject, RequestEngageCompCon, RequestTakeBackCompCon, RequestFullTech, RequestGrappleBreak, RequestGrappleContest, RequestGrappleRelease, RequestHide, RequestJockey, RequestJockeyMenu, RequestMount, RequestOvercharge, RequestReload, RequestRide, RequestRideDismount, RequestSearch, RequestSelfDestruct, RequestShakeOff, RequestShutDown, RequestStabilize, StabilizePrimary, StabilizeSecondary, StopBarrageAsSkirmish, WireCell } from "@lancer-vtt/shared";

/**
 * The up-front declarations the skirmish bar collects — the recorded-choice half of the
 * resolution, mirroring the CLI's `--weapon`/`--profile`/`--accuracy`/`--choice`. All optional: a
 * bare attack (default weapon, +0 accuracy, no choices) resolves in one round-trip with no modal.
 */
export type AttackDeclaration = {
  /** Weapon id (`--weapon`). Absent = the resolver's default/unarmed path. */
  weapon?: string;
  /** Alternate firing profile id (`--profile`), e.g. Spin-Up. */
  profile?: string;
  /** Net accuracy/difficulty (`--accuracy`): +acc / −diff / 0. */
  accuracy?: number;
  /** Pre-declared `choose` answers keyed by prompt id (`--choice prompt=opt`). */
  choices?: Record<string, string>;
  /** A ranged-or-melee weapon's mode; the aim sends the one latched this turn (`weaponModeThisTurn`, `attackRange.ts`). */
  mode?: WeaponMode;
  /**
   * Fire this attack as a **reaction** (`--overwatch`) — the NPC control surface's Overwatch row
   * arms the SAME Skirmish flow with this set, so a
   * GM-declared Overwatch rides the identical weapon/profile/accuracy pick + board-click target;
   * the authority spends the reaction slot + the named "overwatch" 1/round cap
   * (`checkOverwatchRange`) instead of an action point. Absent/false = the ordinary standard attack.
   */
  reaction?: boolean;
  /**
   * Fire this attack as a **tech attack** — `weapon` names
   * a tech-attack action id (a system-granted composite `<itemId>:<actionIndex>` id, e.g.
   * `"ms_markerlight:0"`, or `"act_invade"`) and the room resolves it through the tech-attack spine
   * (`checkTechRange`/`checkTechLoS`/tech-immunity) rather than a weapon attack. Mirrors
   * `quickTechIntent`'s own `tech_attack` route (`@lancer-vtt/rules`), which is the only OTHER
   * producer of `RequestAttack.tech` on the wire today — that one is table-driven off
   * `QUICK_TECH_OPTIONS` and can only ever name `act_invade`, so it can't address a composite id;
   * this field is what lets a system-granted tech attack (COMBAT.app's granted-row `route:"attack"`
   * click, `CombatApp.tsx`'s `fireAt`) go through the SAME one `buildAttackIntent` construction
   * site instead of a second hand-built `request_attack` literal. Absent/false = an ordinary
   * (possibly unarmed) weapon attack.
   */
  tech?: boolean;
}

/**
 * Build a `request_attack` intent from the attacker, the {@link AttackTarget} (a clicked unit or a
 * bare AoE-origin cell), and the up-front {@link AttackDeclaration}. Only non-default fields are
 * included so the wire stays minimal (and an empty `choices` is dropped, so a no-choice attack omits
 * it entirely — the authority's no-prompt fast path).
 */
export function buildAttackIntent(
  attackerId: string,
  target: AttackTarget,
  decl: AttackDeclaration = {},
): RequestAttack {
  const intent: RequestAttack = {
    type: "request_attack",
    attacker: attackerId,
    target,
    accuracy: decl.accuracy ?? 0,
  };
  if (decl.weapon) intent.weapon = decl.weapon;
  if (decl.profile) intent.profile = decl.profile;
  if (decl.choices && Object.keys(decl.choices).length > 0) intent.choices = decl.choices;
  if (decl.mode) intent.mode = decl.mode;
  if (decl.reaction) intent.reaction = true;
  if (decl.tech) intent.tech = true;
  return intent;
}

// ── Barrage (the mech Full-attack — roll-as-you-assign, `barrage-sequential-resolution`) ──
//
// The Barrage wire shape is ONE shot (`request_barrage` = a single weapon at a single target, resolved
// immediately) plus `end_attack` (the abandon-after-one close, shared with the other locked
// attacks), held together by the server's synced mid-attack lock — NOT a client-packed bundle. The
// browser drives it board-click roll-as-you-assign: the in-panel `BarrageChooser` arms a weapon
// card, the next board click fires THAT one
// shot through {@link buildBarrageIntent}, the weapon auto-disarms, and the lock (read from synced
// state) drives the remaining legal cards + an "End Barrage" button ({@link buildEndAttackIntent}).
// Opener-vs-continuation is auto-detected server-side off the lock — these builders only advise the
// wire (the **client never resolves**); the engine is the wall.

/**
 * Build a `request_barrage` intent — ONE Barrage shot.
 * The named `decl.weapon` is required on the wire (a Barrage names a real loadout weapon; the weaponless
 * fallback is a CLI/engine path) — an absent one packs `""` (the server's weaponless path). `profile`/
 * `accuracy` mirror {@link AttackDeclaration}. The server disambiguates opener (charges the Full action,
 * opens the lock) from continuation (no second charge, auto-closes when no legal attack remains) off the
 * synced lock — no flag on the wire.
 */
export function buildBarrageIntent(
  attackerId: string,
  target: AttackTarget,
  decl: AttackDeclaration & { mounts?: readonly string[] } = {},
): RequestBarrage {
  const intent: RequestBarrage = {
    type: "request_barrage",
    attacker: attackerId,
    weapon: decl.weapon ?? "",
    target,
    accuracy: decl.accuracy ?? 0,
  };
  if (decl.profile) intent.profile = decl.profile;
  if (decl.choices && Object.keys(decl.choices).length > 0) intent.choices = decl.choices;
  if (decl.mounts && decl.mounts.length > 0) intent.mounts = [...decl.mounts];
  return intent;
}

/**
 * Build a `stop_barrage_as_skirmish` intent — the fire card's STOP FIRING on a Barrage that fired
 * one mount (R7): the room re-prices the Full action as a Skirmish's Quick and closes the lock.
 */
export function buildStopBarrageAsSkirmishIntent(attackerId: string): StopBarrageAsSkirmish {
  return { type: "stop_barrage_as_skirmish", attacker: attackerId };
}

/**
 * Build an `end_attack` intent — the explicit abandon-after-one close, shared by every verb that
 * rides the attack lock, not just Barrage.
 */
export function buildEndAttackIntent(attackerId: string): EndAttack {
  return { type: "end_attack", attacker: attackerId };
}

// ── Stabilize (the mech self-maintenance Full action) ──
//
// Stabilize is the deterministic (no-dice) self-maintenance Full action (Core Book p.70): choose ONE
// primary (Cool: clear heat + Exposed | Patch: spend a repair → full HP) and ONE secondary (reload |
// clear Burn | clear one condition | end one save-ending effect), collected UP FRONT in one round-trip
// (the BarrageChooser pattern — both menus are known at action time). The intent already carries both,
// so the browser just packs them. As with the attack builders, the **client never resolves**: it only
// advises the wire — the authority applies the chosen steps and (for Patch) is the wall at 0 repairs
// (`checkStabilizeLegality`); the chooser only mirrors that affordance, never re-enforces it.

/**
 * The up-front Stabilize declaration the chooser collects (mirrors the engine's `StabilizeChoice`): the
 * chosen primary, the optional secondary, and the `secondaryStatus` the `clear_condition` /
 * `end_save_effect` secondaries name.
 */
export type StabilizeDeclaration = {
  primary: StabilizePrimary;
  secondary?: StabilizeSecondary;
  /** The specific condition / save-ending effect name (only read for `clear_condition` / `end_save_effect`). */
  secondaryStatus?: string;
}

/**
 * Build a `request_stabilize` intent from the actor and the up-front {@link StabilizeDeclaration}. The
 * `secondaryStatus` is folded in only for the `clear_condition` / `end_save_effect` secondaries and only
 * when named — the wire stays minimal, and the authority resolves the bundle into ONE `apply_resolved`
 * (`kind: "stabilize"`) charged as one Full action (one logical undo). Re-exports
 * `StabilizePrimary`/`StabilizeSecondary` so a caller imports the choice types from one place.
 */
export function buildStabilizeIntent(actorId: string, decl: StabilizeDeclaration): RequestStabilize {
  const intent: RequestStabilize = { type: "request_stabilize", actor: actorId, primary: decl.primary };
  if (decl.secondary) intent.secondary = decl.secondary;
  if (decl.secondaryStatus && (decl.secondary === "clear_condition" || decl.secondary === "end_save_effect")) {
    intent.secondaryStatus = decl.secondaryStatus;
  }
  return intent;
}

// ── Overcharge (the mech reactor push, project `overcharge`) ──────────────────
//
// Overcharge is the once-per-turn free action that buys an extra Quick action by paying escalating
// self-heat (1 → 1d3 → 1d6 → 1d6+4; Core Book p.78). Self-only, no choices — a single round-trip
// with no chooser and no prompt. As with the other builders, the **client never resolves**: it only
// advises the wire — the authority rolls the heat at the actor's current escalation step, runs it
// through the A3 heat / Overheating path, advances the step, and grants the +1 Quick action, all in
// ONE atomic undoable unit. The once-per-turn / mech-only / on-turn walls are the authority's
// (`checkOverchargeLegality`); the button only fires the intent, it re-enforces nothing.

/**
 * Build a `request_overcharge` intent for the actor (self only — it pushes its own reactor). No
 * choices and no target, so the intent is just the actor; the authority does all the resolution.
 */
export function buildOverchargeIntent(actorId: string): RequestOvercharge {
  return { type: "request_overcharge", actor: actorId };
}

// ── Full Tech (the mech two-Quick-Tech-options Full action) ──
//
// Full Tech (Core Book p.71) is a Full action that takes TWO Quick Tech options — MAY repeat the
// same one (e.g. Bolster+Bolster) — but, mirroring Barrage, the
// wire shape is ONE pick per `request_full_tech`, resolved and committed IMMEDIATELY, held
// together as one logical Full action by the server's mid-attack lock (`AttackLock`,
// `action: "full_tech"`) — NOT a client-packed two-sub bundle. As with Barrage, the **client
// never resolves**: it only advises the wire — the authority routes the pick through the SAME
// `quickTechIntent` router a single Quick Tech uses, resolves it, and auto-detects opener
// (charge the Full action) vs continuation (no second charge, off the lock) exactly like Barrage.

/**
 * Build a `request_full_tech` intent — ONE Quick Tech pick of a Full Tech, mirroring
 * {@link buildBarrageIntent}. The caller sends this once per pick (may repeat the same `option` across the two picks — RAW Core
 * p.71); the authority is the wall regardless. This pick's own `choices` (e.g. Invade's FRAGMENT
 * SIGNAL menu) ride the plain, un-namespaced map a standalone Quick Tech uses — the two-sub
 * `sub:<i>:<promptId>` namespacing retired with the bundle.
 */
export function buildFullTechIntent(
  actorId: string,
  target: string,
  decl: { option: string; accuracy?: number; choices?: Record<string, string> },
): RequestFullTech {
  const intent: RequestFullTech = { type: "request_full_tech", actor: actorId, option: decl.option, target, accuracy: decl.accuracy ?? 0 };
  if (decl.choices && Object.keys(decl.choices).length > 0) intent.choices = decl.choices;
  return intent;
}

// ── Hide + Search (the mech stealth pair, project `opposed-check-primitive` P3) ──
//
// Hide (Core Book p.74) is a self-only Quick action — no target, no choices, one round-trip like
// Overcharge: the authority auto-checks cover (`checkHideCover`) and hard-rejects in the open. Search
// is the RAW opposed-contest counter — a single-target Quick action (searcher vs. a Hidden mech within
// Sensors + LoS); the browser only advises the wire (attacker + target), the authority draws both d20s
// and resolves the contest (`resolveContest` → `clearHiddenSteps` on a searcher win **or tie**).
// Neither builder needs an `AttackDeclaration`-shaped bag — both intents are just
// `{ actor }` / `{ actor, target }` on the wire.

/** Build a `request_hide` intent — the mech stealth Quick action (self-only; no target, no choices). */
export function buildHideIntent(actorId: string): RequestHide {
  return { type: "request_hide", actor: actorId };
}

/**
 * Build a `request_search` intent from the searcher and its chosen target (the in-panel
 * `SearchControls`'s pick, filtered to Hidden + in-Sensors-range candidates). The authority is the
 * wall for everything else — budget, Sensors, LoS, and the contest itself.
 */
export function buildSearchIntent(actorId: string, targetId: string): RequestSearch {
  return { type: "request_search", actor: actorId, target: targetId };
}

// ── Disengage (the mech movement-immunity Full action) ──
//
// Disengage (Core Book p.74) is a self-only Full action — no target, no choices, one round-trip
// like Overcharge/Hide: the authority carries no legality gate (unlike Hide's cover check) — it
// charges the Full action and lands a first-class `Disengaged` status that (1) suppresses the
// engagement mid-move stop for the rest of the turn and (2) hard-rejects a reaction attack
// targeting the actor. The browser only advises the wire; the authority is the wall.

/** Build a `request_disengage` intent — the mech's movement-immunity Full action (self-only; no target, no choices). */
export function buildDisengageIntent(actorId: string): RequestDisengage {
  return { type: "request_disengage", actor: actorId };
}

// ── Shut Down / Boot Up (the reactor pair, project `shutdown-bootup-selfdestruct` P2 engine / `shut-down-boot-up-browser` P1 browser) ──
//
// Both are self-only, parameter-free one-click actions — the Disengage shape exactly, so both intents
// are just `{ actor }` on the wire. They are ASYMMETRIC in cost: Shut Down is a **Quick** action
// (`economy.ts:232`), Boot Up a **Full** one (`:246`), so they hang off DIFFERENT columns of the action
// grid. The authority stays the wall: Shut Down carries no legality precondition (RAW p.71 attaches
// none) and lands the composite `Shut Down` status (all heat + Exposed cleared, every tech-caused
// status ended, Stunned indefinitely + hard tech-immunity); Boot Up hard-rejects an
// actor that isn't Shut Down (`checkBootUpLegality`) and its only effect is clearing that status.
// Both are routed off the room's generic gate ladder into `refereeSelfAction` — the client never
// resolves, and a refusal surfaces through the categorized rejection toast.

/** Build a `request_shutdown` intent — the mech's reactor self-shutdown Quick action (self-only; no target, no choices). */
export function buildShutDownIntent(actorId: string): RequestShutDown {
  return { type: "request_shutdown", actor: actorId };
}

/** Build a `request_boot_up` intent — the mech's reactor-restart Full action (self-only; no target, no choices). */
export function buildBootUpIntent(actorId: string): RequestBootUp {
  return { type: "request_boot_up", actor: actorId };
}

// ── Self-Destruct (the reactor-meltdown countdown, project `shutdown-bootup-selfdestruct` P3 engine /
//    `self-destruct-browser` P1 browser, RAW Core p.72) ──
//
// Self-Destruct is a self-only **Quick** action that arms (or re-arms) the mech's reactor-meltdown
// countdown at an up-front `at` ∈ {1,2,3} — RAW's "your choice" of "the end of your next turn, or one
// of your turns within the following two rounds". The authority owns the whole countdown/detonation
// (`selfDestructSteps`/`meltdownDue`/`resolveMeltdownBlast`, `packages/rules/src/encounter/meltdown.ts`)
// and the sole legality wall (`checkSelfDestructLegality` — `at`'s 1..3 range, nothing else; RAW attaches
// no cancel clause). This builder only advises the wire — the client never resolves.

/** The up-front Self-Destruct declaration the chooser collects: the chosen detonation turn. */
export type SelfDestructDeclaration = {
  /** Turns until detonation (RAW's three-turn "your choice") — becomes the wire `at` / `meltdown_countdown`. */
  at: 1 | 2 | 3;
}

/** Build a `request_self_destruct` intent — arm/re-arm the reactor-meltdown countdown (self-only; no target). */
export function buildSelfDestructIntent(actorId: string, decl: SelfDestructDeclaration): RequestSelfDestruct {
  return { type: "request_self_destruct", actor: actorId, countdown: decl.at };
}

// ── Dismount / Eject (the mech↔pilot bridge, RAW Core p.70) ──
//
// Both are the mech's own actions — the pilot climbs out (Dismount, a **Full** action, an adjacent
// free space only) or is flung clear (Eject, a **Quick** action, anywhere within fly 6) — and both
// spawn the on-foot pilot combatant, land `Unpiloted` (+ `Impaired` and the Eject lockout for Eject),
// and END THE MECH'S TURN. Unlike every other builder in this file, both
// carry an up-front `at: WireCell` — the landing cell the player picks by clicking the board, the
// `activate_cell` shape (arm → click a bare/occupied cell → commit), not a token-fire gesture. The
// authority is the sole wall (`checkEjectLegality`/`checkDismountLegality`, `@lancer-vtt/rules`) —
// adjacency/fly-6 reach, a free Size-0.5 landing space, not-already-Unpiloted, and (Eject only) the
// lockout resource; these builders only pack the wire intent.

/** Build a `request_dismount` intent — the mech's pilot-bridge **Full** action (`actor` the mech; `at` the adjacent free cell the pilot climbs out to). */
export function buildDismountIntent(actorId: string, at: WireCell): RequestDismount {
  return { type: "request_dismount", actor: actorId, at };
}

/** Build a `request_eject` intent — the mech's pilot-bridge **Quick** action (`actor` the mech; `at` the landing cell, within fly 6). */
export function buildEjectIntent(actorId: string, at: WireCell): RequestEject {
  return { type: "request_eject", actor: actorId, at };
}

// ── Mount (the pilot's own return trip across the mech↔pilot bridge, RAW Core p.70) ──
//
// Unlike Dismount/Eject, Mount is the on-foot PILOT's own **Full** action, not the mech's: `actor` is
// the pilot, `mechId` is the target mech (its own, Unpiloted, adjacent) — a **unit pick by id**, not a
// board-cell commit, so this builder carries no `WireCell` at all. The authority is the sole wall
// (`checkMountLegality`, `@lancer-vtt/rules`) — mech kind, Unpiloted with `partner` pointing at this
// pilot, and adjacency; this builder only packs the wire intent.

/** Build a `request_mount` intent — the on-foot pilot's pilot-bridge **Full** action (`actor` the pilot; `mechId` its own adjacent Unpiloted mech). */
export function buildMountIntent(actorId: string, mechId: string): RequestMount {
  return { type: "request_mount", actor: actorId, mechId };
}

// ── Reload (the on-foot pilot's own Quick action, RAW Core p.75) ──
//
// Reload is a self-only one-click action (Hide/Disengage's shape). `weapon` is OPTIONAL on the
// wire (`RequestReload`, `@lancer-vtt/shared`): absent, the authority reloads the actor's FIRST
// Loading pilot weapon (`checkReloadLegality`, `@lancer-vtt/rules`), and refuses when that one is
// already loaded. The command menu names the weapon, so a pilot whose second Loading weapon is the
// unloaded one reloads the right one.

/** Build a `request_reload` intent — the on-foot pilot's Reload Quick action (self-only; no target), naming the weapon instance key when given. */
export function buildReloadIntent(actorId: string, weapon?: string): RequestReload {
  return { type: "request_reload", actor: actorId, ...(weapon !== undefined ? { weapon } : {}) };
}

// ── Jockey / Ride / Ride-Dismount (the rider-attachment verbs, RAW Core p.70/75) ──
//
// Jockey and Ride are both **UNIT picks by id** (Mount's exact shape — never a `WireCell`):
// Jockey targets an adjacent HOSTILE mech (a contested GRIT-vs-HULL attach, resolved entirely
// server-side — this builder packs no roll); Ride targets an adjacent WILLING ALLIED mech (no
// contest). Ride-Dismount is the ONE cell pick of the three: `at` is the
// PLAYER-CHOSEN adjacent landing space (RAW: "you are placed in an adjacent space"), while the
// carrier itself is read off the rider's own `Riding.partner` server-side — never named on the
// wire, mirroring Mount's own "the mech is implied, not picked" half but with an explicit `at`
// exactly like `RequestDismount`.

/** Build a `request_jockey` intent — the on-foot pilot's Jockey Full action (an adjacent hostile mech, by id). */
export function buildJockeyIntent(actorId: string, targetId: string): RequestJockey {
  return { type: "request_jockey", actor: actorId, target: targetId };
}

/** Build a `request_ride` intent — the on-foot pilot's consent-based allied Ride Full action (an adjacent, same-side mech, by id). */
export function buildRideIntent(actorId: string, targetId: string): RequestRide {
  return { type: "request_ride", actor: actorId, target: targetId };
}

/** Build a `request_ride_dismount` intent — the riding pilot's explicit allied-Dismount Full action (a player-chosen adjacent free cell; the carrier is read server-side off the rider's own `Riding` marker). */
export function buildRideDismountIntent(actorId: string, at: WireCell): RequestRideDismount {
  return { type: "request_ride_dismount", actor: actorId, at };
}

/** Build a `request_jockey_menu` intent — the Jockeying rider's repeatable Full action (Core p.75); the carrier is read server-side off its `Jockeying` marker. */
export function buildJockeyMenuIntent(actorId: string, choice: RequestJockeyMenu["choice"]): RequestJockeyMenu {
  return { type: "request_jockey_menu", actor: actorId, choice };
}

/** Build a `request_shake_off` intent — the Jockeyed mech's contested Full action to throw its rider (Core p.75); the rider is read server-side off its `Jockeyed` marker. */
export function buildShakeOffIntent(actorId: string): RequestShakeOff {
  return { type: "request_shake_off", actor: actorId };
}

// ── Engage COMP/CON (the AI/cascade subsystem's voluntary protocol) ──
//
// Engage COMP/CON (Core Book p.71) is a self-only **Protocol** — no target, no choices, one round-trip
// like Overcharge/Disengage: the authority is the sole wall (`checkEngageCompConLegality`) — it rejects
// a mech carrying no cascade-immune AI system (`tg_ai`+`tg_no_cascade`) and a mech already Engaged, then lands the persistent
// `COMP/CON Engaged` status (0 action points). The browser only advises the wire; the authority stays the wall.

/** Build a `request_engage_compcon` intent — the mech's Engage COMP/CON protocol (self-only; no target, no choices). */
export function buildEngageCompConIntent(actorId: string): RequestEngageCompCon {
  return { type: "request_engage_compcon", actor: actorId };
}

/** Build a `request_take_back_compcon` intent — the pilot's protocol taking control back from COMP/CON (Core p.106; self-only). */
export function buildTakeBackCompConIntent(actorId: string): RequestTakeBackCompCon {
  return { type: "request_take_back_compcon", actor: actorId };
}

// ── Grapple break/release (the mech grapple-control pair) ──
//
// Grapple's initiation needs no dedicated builder — it reuses {@link buildAttackIntent} with the
// synthetic `act_grapple` weapon, the exact Ram shape (no `choices`, no Size gate). Once bound, the
// two follow-up intents are self-only, no-target, one-click actions (mirroring Overcharge/Hide's
// shape): the defender's contested-HULL Quick action (`request_grapple_break`, resolved via
// `resolveContest` server-side) and the attacker's unconditional free-action release
// (`request_grapple_release`). Both intents are just `{ actor }` on the wire — the authority reads
// the actor's own `Grappled.partner` (`@lancer-vtt/rules`'s `grapplePartner`) to find the other side;
// the client never resolves.

/** Build a `request_grapple_break` intent — the Grapple defender's contested-HULL Quick action. */
export function buildGrappleBreakIntent(actorId: string): RequestGrappleBreak {
  return { type: "request_grapple_break", actor: actorId };
}

/**
 * Build a `request_grapple_contest` intent — a same-Size pair's start-of-turn contested HULL check
 * (Core p.69), free and once per turn; the CLI's `grapple-contest` sends the same shape.
 */
export function buildGrappleContestIntent(actorId: string): RequestGrappleContest {
  return { type: "request_grapple_contest", actor: actorId };
}

// ── Detonate Mine ──
//
// The deployer's own remote-only Quick action on a mine it placed (Core p.106: "This charge must
// be remotely detonated as a quick action"). `actorId` names the DEPLOYER combatant
// (`mineProfile.deployedBy`), NOT the clicking viewer's slot — a GM detonating a player's mine
// still attributes the intent to that player's combatant, exactly like the CLI verb
// (`cli/src/encounter-commands.ts`'s `requestDetonateMine`). The server is the sole wall
// (`checkMineDetonateLegality`: `target`'s `mineProfile.deployedBy === actor`, server-verified);
// this builder only advises the wire.

// ── Detect / Disarm Mine ──
//
// Any character's Quick actions against MINES (Core p.106): a SYSTEMS check that reveals every
// hidden mine within Sensors, and a SYSTEMS check on an adjacent mine that removes it, or on a
// failure detonates it. The CLI's `detect-mine` / `disarm-mine` send the same shapes. Detect names
// no mine, since the searcher's own state can't hold a hidden one to name.

/** Build a `request_detect_mine` intent — `actorId`'s Quick sweep for hidden mines within its Sensors. */
export function buildDetectMineIntent(actorId: string): RequestDetectMine {
  return { type: "request_detect_mine", actor: actorId };
}

/** Build a `request_disarm_mine` intent — `actorId`'s Quick attempt on the adjacent mine `mineId`. */
export function buildDisarmMineIntent(actorId: string, mineId: string): RequestDisarmMine {
  return { type: "request_disarm_mine", actor: actorId, target: mineId };
}

/** Build a `request_detonate_mine` intent — the deployer's remote-only Quick detonation of its own placed mine (`actorId` = the deployer combatant, `targetId` = the mine). */
export function buildDetonateMineIntent(actorId: string, targetId: string): RequestDetonateMine {
  return { type: "request_detonate_mine", actor: actorId, target: targetId };
}

/** Build a `request_grapple_release` intent — the Grapple attacker's unconditional free-action end. */
export function buildGrappleReleaseIntent(actorId: string): RequestGrappleRelease {
  return { type: "request_grapple_release", actor: actorId };
}

// ── Release a held (prepared) action ──
//
// Release (Core pp. 71-72, "as a reaction, you may … take the readied action") rides the SAME
// `request_attack` wire shape a live attack takes, with one added field: `releasedBy` — the
// preparer whose armed hold this resolves out of turn (`shared/src/index.ts`'s `RequestAttack.
// releasedBy`, `backend/src/encounterRoom.ts`'s `resolveRelease`). Release is attack-shaped only:
// Skirmish/Ram/Grapple/Improvised Attack, or the Invade flavor of Quick Tech — exactly the verbs
// `isAttackShapedHeld` accepts.

/**
 * The weapon/tech pair a release must send so `resolveRelease`'s id/weapon-match passes —
 * mirrors the CLI `release` verb's own derivation (`actionIdForAttackLike`,
 * `packages/rules/src/encounter/economy.ts`) so the caller never has to know the synthetic
 * weapon a Ram/Grapple/Improvised/Invade hold implies. Skirmish carries a REAL loadout weapon
 * on `held.weapon` (required at prepare); the synthetic-weapon verbs pin their
 * `act_*` weapon through the id-match itself, and Invade is forced to `act_invade` already.
 */
function releaseWeaponFor(held: HeldAction): { weapon?: string; tech?: boolean } {
  switch (held.readiedActionId) {
    case "ram":
      return { weapon: "act_ram" };
    case "grapple":
      return { weapon: "act_grapple" };
    case "improvised_attack":
      return { weapon: "act_improvised_attack" };
    case "quick_tech":
      return { weapon: held.weapon ?? "act_invade", tech: true };
    default:
      // "skirmish" — `held.weapon` is guaranteed present (the weapon is required at prepare).
      return { weapon: held.weapon };
  }
}

/**
 * Build the `request_attack` intent that RELEASES `preparerId`'s armed `held` action.
 * Derives the weapon/tech pair off `held` ({@link
 * releaseWeaponFor}) and stamps `releasedBy: preparerId` so the room resolves it as a release —
 * skipping every gate `prepare` already charged — rather than an ordinary standard attack. As
 * with every other builder here, the **client never resolves**: the authority re-checks
 * fire-time-variable legality (range/target/loaded) and is the wall on an illegal release — a
 * rejected release is a no-op, and the hold stays armed for a retry. No up-front
 * `AttackDeclaration` param (unlike {@link buildAttackIntent}) — a release has nothing left to
 * declare beyond the target; the readied verb/weapon/accuracy were already pinned at prepare.
 */
export function buildReleaseIntent(preparerId: string, target: AttackTarget, held: HeldAction): RequestAttack {
  const { weapon, tech } = releaseWeaponFor(held);
  const intent = buildAttackIntent(preparerId, target, { weapon });
  if (tech) intent.tech = true;
  intent.releasedBy = preparerId;
  return intent;
}

/** The up-front declarations the activate bar collects. */
export type ActivateDeclaration = {
  /** Optional target combatant the action binds to (absent = self-action). */
  target?: string;
  /** Pre-declared `choose` answers keyed by prompt id (`--choice prompt=opt`). */
  choices?: Record<string, string>;
  /**
   * The **template-center** cell for an area/deploy action whose `for_each` selector reads
   * `origin: "site"` — Smoke/HEX Charges thrown to a point. Supplied by the action bar's cell-pick aim mode; the
   * CLI's `activate --site x,y` is the scriptable twin. Absent for a self/adjacent action.
   */
  site?: WireCell;
  /** The **direction** cell for a line/cone area action (wire-stable; v1 enumerates disk-only). */
  toward?: WireCell;
  /**
   * The **directed-placement** picks — the ordered cells a multi-pick `deploy_object` draft
   * accumulated client-side (`combatInspector.ts`'s `ActionDraft["activate_sites"].picks`),
   * threaded straight onto `RequestActivate.sites`. Supplied only on the FINAL pick: nothing is
   * sent until the draft is complete, and ONE intent carries every cell as one atomic resolution,
   * never a per-pick send. Absent for every other activation, including the single-cell `site` above (a
   * DIFFERENT action shape — a point-blanket throw's template center, not a directed multi-pick
   * placement; the two never coexist on one intent).
   */
  sites?: WireCell[];
}

/**
 * Build a `request_activate` intent from the actor, the action id, and the up-front
 * {@link ActivateDeclaration}. Pre-declaring a `choose` answer here resolves the activation in one
 * round-trip with no modal; omitting it lets the authority raise the prompt.
 */
export function buildActivateIntent(
  actorId: string,
  actionId: string,
  decl: ActivateDeclaration = {},
): RequestActivate {
  const intent: RequestActivate = {
    type: "request_activate",
    actor: actorId,
    action: actionId,
  };
  if (decl.target) intent.target = decl.target;
  if (decl.site) intent.site = decl.site;
  if (decl.sites && decl.sites.length > 0) intent.sites = decl.sites;
  if (decl.toward) intent.toward = decl.toward;
  if (decl.choices && Object.keys(decl.choices).length > 0) intent.choices = decl.choices;
  return intent;
}

/** Re-export the union + the attack-target shape so callers building either intent import from one place. */
export type { AttackTarget, EncounterIntent, StabilizePrimary, StabilizeSecondary };

/**
 * The selectable firing profiles of a frozen weapon — the default profile (the weapon
 * itself) plus any alternates (`profiles`). Used to offer a profile picker only when a weapon
 * actually has alternates. Returns `[{ id, name }]` ready for a `<select>`; a single-profile
 * weapon returns just its default.
 */
export function weaponProfiles(weapon: WeaponProfile): { id: string; name: string }[] {
  const out = [{ id: weapon.id, name: `${weapon.name} (default)` }];
  for (const p of weapon.profiles ?? []) out.push({ id: p.id, name: p.name });
  return out;
}

/** One firing-profile pill: `pid` is the draft's `profileId` value it sets (`""` is the default). */
export type ProfilePill = { id: string; name: string; pid: string; selected: boolean; locked: boolean };

/**
 * The profile pills for an armed weapon. With no `active` (the projected
 * `ProjectedEncounterState.activeProfiles` entry), every profile is a free pick and the draft's
 * `profileId` marks the selected one. With `active`, state sets the profile (the Assault Cannon's
 * Spin-Up Mode): only the alternates show, the live one is selected, and every pill is locked,
 * because the draft sends no profile and the room fires the state-selected one. Empty for a
 * single-profile weapon.
 */
export function profilePills(weapon: WeaponProfile, profileId: string, active: string | undefined): ProfilePill[] {
  const all = weaponProfiles(weapon);
  if (all.length <= 1) return [];
  if (active === undefined) return all.map((p) => ({ ...p, pid: p.id === weapon.id ? "" : p.id, selected: profileId === (p.id === weapon.id ? "" : p.id), locked: false }));
  // The base weapon is frozen from its first profile, so the two ids name one mode.
  const live = active === weapon.id ? weapon.profiles?.[0]?.id : active;
  return all.slice(1).map((p) => ({ ...p, pid: "", selected: p.id === live, locked: true }));
}
